import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import { sendMagicLink } from '@/lib/auth.ts'
import type { AuthUser } from '@/lib/auth.ts'

interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string) => Promise<{ success: true }>
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

/** ログイン中の人。AuthProvider の外（テストなど）では null を返し、例外にしない。 */
export function useOptionalAuthUser(): AuthUser | null {
  return useContext(AuthContext)?.user ?? null
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}

interface MeResponse {
  user: { sub: string; email: string; name: string } | null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    // Identity comes from the BFF-owned HttpOnly session cookie (set by the
    // atsume.id OIDC callback). No Supabase auth involved.
    fetch('/api/auth/me', { credentials: 'include', headers: { accept: 'application/json' } })
      .then((res) => (res.ok ? (res.json() as Promise<MeResponse>) : null))
      .then((data) => {
        if (cancelled) return
        if (data?.user?.email) {
          setUser({ email: data.user.email, name: data.user.name || data.user.email.split('@')[0] })
        }
      })
      .catch(() => {
        // No session / network error → unauthenticated.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const login = useCallback(
    async (email: string): Promise<{ success: true }> => {
      return sendMagicLink(email)
    },
    [],
  )

  const logout = useCallback(() => {
    setUser(null)
    // Clear the BFF session cookie; ignore the result (best-effort).
    void fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {})
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: user !== null,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
